import { ChangeEvent, useRef, DragEvent, useState } from 'react';

import { useController, UseControllerProps } from 'react-hook-form';
import { FormHelperText } from 'shared/mui/FormHelperText/FormHelperText';
import { FileInputWrapperStyled } from 'shared/react-admin/Inputs/FileInput/FileInput.styled';
import { FileInputPlaceholder } from 'shared/react-admin/Inputs/FileInput/FileInputPlaceholder/FileInputPlaceholder';
import { FileInputUpload } from 'shared/react-admin/Inputs/FileInput/FileInputUpload/FileInputUpload';

interface FileInputProps extends UseControllerProps {
  accept?: string;
  disabled?: boolean;
}

export const FileInput = ({
  name,
  control,
  rules,
  defaultValue,
  disabled = false,
  accept = '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}: FileInputProps) => {
  const {
    field: { value, onChange, onBlur },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const [isDragging, setIsDragging] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const handleChangeFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (!selectedFile) {
      return;
    }

    onChange(selectedFile);
    event.target.value = '';
  };

  const handleDeleteFile = () => {
    onChange(null);
    onBlur();

    if (ref.current) {
      ref.current.value = '';
    }
  };

  const handleClickUpload = () => {
    if (!disabled) {
      ref?.current?.click();
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(false);

    if (disabled) {
      return;
    }

    const files = e.dataTransfer?.files;

    if (files && files.length > 0) {
      const file = files[0];
      onChange(file);
      onBlur();
    }
  };

  return (
    <>
      <input
        accept={accept}
        disabled={disabled}
        onChange={handleChangeFile}
        ref={ref}
        style={{ display: 'none' }}
        type="file"
      />
      <FileInputWrapperStyled
        disabled={disabled}
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {!value && <FileInputUpload onClick={handleClickUpload} />}
        {value && (
          <FileInputPlaceholder
            name={value instanceof File ? value?.name : ''}
            onDelete={disabled ? undefined : handleDeleteFile}
          />
        )}
      </FileInputWrapperStyled>
      {error && <FormHelperText error={true}>{error.message}</FormHelperText>}
    </>
  );
};
